import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUp, Linkedin } from "lucide-react";
import photo from "../../assets/conference/min-DSC06463fx.jpg";

/**
 * Les questions de la salle — page publique ouverte par le QR code de la slide 3.
 * Voir docs/conferences/seminaire-QUESTIONS-SALLE.md.
 *
 * C'est le seul moment de la conférence où la salle AGIT : la page prolonge le
 * deck au lieu de ressembler à un formulaire.
 *  - une étape par écran, comme une appli : fréquence → mot → question ;
 *  - la fréquence est une CUVE qu'on remplit, comme les bassins de l'usine ;
 *  - la question passe par un GOULOT, et la page répond par son rang ;
 *  - la carte d'Adrien arrive juste après le premier envoi : c'est le moment où
 *    la personne est la plus réceptive, pas avant.
 *
 * Elle reste ouverte sur le téléphone pendant toute la conférence : après le
 * profil (une seule fois par téléphone), on revient toujours sur la question.
 *
 * ⚠️ index.css impose des couleurs globales à h1, h2 et p (dont un p presque
 * noir) : chaque titre et paragraphe porte ici sa propre couleur.
 *
 * `?s=repetition` sépare les répétitions du jour J.
 * `?c=cdc18` porte le code de la salle : celui qui scanne le QR n'a rien à
 * taper. Sans lui, le serveur refuse l'envoi (`code_requis`) et la page
 * demande le code affiché à l'écran — sans rien perdre de ce qui était saisi.
 */

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
const LINKEDIN = "https://www.linkedin.com/in/adrien-giguelay-96975987";

// Du bas de la cuve vers le haut.
const FREQUENCES = [
  { cle: "jamais", libelle: "Jamais" },
  { cle: "mois", libelle: "Quelques fois par mois" },
  { cle: "semaine", libelle: "Quelques fois par semaine" },
  { cle: "jour", libelle: "1 à 2 fois par jour" },
  { cle: "plus3", libelle: "3 fois par jour ou plus" },
];

// Le stockage peut être bloqué (navigation privée) : la page marche sans.
const lire = (cle) => {
  try { return localStorage.getItem(cle); } catch { return null; }
};
const ecrire = (cle, valeur) => {
  try { localStorage.setItem(cle, valeur); } catch { /* sans conséquence */ }
};

const DEGRADE = "bg-gradient-to-r from-blue-400 via-cyan-300 to-violet-400 bg-clip-text text-transparent";

/* ───────────────────────── Décor ───────────────────────── */

function Fond() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden">
      <div className="absolute -top-32 -left-24 h-80 w-80 rounded-full bg-blue-600/25 blur-3xl animate-pulse" />
      <div className="absolute top-1/3 -right-32 h-96 w-96 rounded-full bg-violet-600/20 blur-3xl animate-pulse [animation-delay:1.5s]" />
      <div className="absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl animate-pulse [animation-delay:3s]" />
      {/* Grille discrète, celle d'un plan d'usine. */}
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(#fff_1px,transparent_1px),linear-gradient(90deg,#fff_1px,transparent_1px)] [background-size:32px_32px]" />
    </div>
  );
}

function Direct({ n }) {
  if (!n) return null;
  return (
    <div className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-300">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
      </span>
      En direct · {n} contribution{n > 1 ? "s" : ""}
    </div>
  );
}

function Progression({ etape }) {
  const etapes = ["frequence", "mot", "question"];
  const i = etapes.indexOf(etape);
  return (
    <div className="flex gap-1.5" aria-hidden="true">
      {etapes.map((e, k) => (
        <div
          key={e}
          className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
            k <= i ? "bg-gradient-to-r from-blue-400 to-cyan-300" : "bg-white/10"
          }`}
        />
      ))}
    </div>
  );
}

/* ───────────────────────── Étape 1 : la cuve ───────────────────────── */

function Cuve({ valeur, onChange, reduit }) {
  const index = FREQUENCES.findIndex((f) => f.cle === valeur);
  const niveau = index < 0 ? 0 : (index + 1) / FREQUENCES.length;

  return (
    <div className="flex items-stretch gap-4">
      {/* La cuve elle-même */}
      <div className="relative w-24 shrink-0 overflow-hidden rounded-b-3xl rounded-t-lg border-2 border-cyan-300/40 bg-white/[0.03] shadow-[0_0_40px_-10px_rgba(34,211,238,0.5)]">
        <motion.div
          className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-blue-600 via-cyan-500 to-cyan-300"
          initial={false}
          animate={{ height: `${niveau * 100}%` }}
          transition={reduit ? { duration: 0 } : { type: "spring", stiffness: 70, damping: 14 }}
        >
          {/* La surface qui ondule */}
          {niveau > 0 && !reduit && (
            <motion.svg
              viewBox="0 0 120 12"
              preserveAspectRatio="none"
              className="absolute -top-2.5 left-0 h-3 w-[200%] text-cyan-300"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ repeat: Infinity, duration: 2.4, ease: "linear" }}
            >
              <path
                fill="currentColor"
                d="M0 6 Q 15 0 30 6 T 60 6 T 90 6 T 120 6 V12 H0 Z"
              />
            </motion.svg>
          )}
        </motion.div>
        {/* Graduations */}
        {FREQUENCES.slice(0, -1).map((_, k) => (
          <div
            key={k}
            className="absolute left-0 h-px w-3 bg-white/30"
            style={{ bottom: `${((k + 1) / FREQUENCES.length) * 100}%` }}
          />
        ))}
      </div>

      {/* Les niveaux, du haut vers le bas pour se lire comme la cuve */}
      <div className="flex flex-1 flex-col-reverse gap-2" role="radiogroup" aria-label="Fréquence d'usage de l'IA">
        {FREQUENCES.map(({ cle, libelle }, k) => {
          const actif = cle === valeur;
          const rempli = index >= k;
          return (
            <button
              key={cle}
              type="button"
              role="radio"
              aria-checked={actif}
              // Pas de bascule : un double tap désélectionnerait sans qu'on le voie.
              onClick={() => onChange(cle)}
              className={`flex min-h-[52px] items-center rounded-xl border px-4 text-left text-[15px] transition-all duration-300 ${
                actif
                  ? "border-cyan-300 bg-cyan-400/15 font-semibold text-white shadow-[0_0_24px_-6px_rgba(34,211,238,0.7)]"
                  : rempli
                  ? "border-cyan-300/20 bg-cyan-400/[0.06] text-cyan-100"
                  : "border-white/10 bg-white/[0.03] text-slate-300"
              }`}
            >
              {libelle}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────── Étape 3 : le goulot ───────────────────────── */

function Goulot({ passage, reduit }) {
  // `passage` change à chaque envoi : une goutte traverse le goulot.
  return (
    <div className="relative mx-auto h-24 w-40" aria-hidden="true">
      <svg viewBox="0 0 160 96" className="absolute inset-0 h-full w-full">
        <defs>
          <linearGradient id="goulot" x1="0" x2="1">
            <stop offset="0" stopColor="#60a5fa" />
            <stop offset="0.5" stopColor="#67e8f9" />
            <stop offset="1" stopColor="#a78bfa" />
          </linearGradient>
        </defs>
        <path
          d="M8 4 H152 L92 52 V92 H68 V52 Z"
          fill="rgba(255,255,255,0.03)"
          stroke="url(#goulot)"
          strokeWidth="2"
          strokeLinejoin="round"
          opacity="0.7"
        />
      </svg>
      <AnimatePresence>
        {passage > 0 && !reduit && (
          <motion.div
            key={passage}
            className="absolute left-1/2 h-3 w-3 -translate-x-1/2 rounded-full bg-cyan-300 shadow-[0_0_16px_4px_rgba(103,232,249,0.8)]"
            initial={{ top: 0, opacity: 0, scale: 1.6 }}
            animate={{ top: [0, 44, 84], opacity: [0, 1, 0], scale: [1.6, 0.8, 0.5] }}
            transition={{ duration: 1.1, times: [0, 0.55, 1], ease: "easeIn" }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ───────────────────────── La carte d'Adrien ───────────────────────── */

function Carte({ enAvant }) {
  return (
    <motion.a
      href={LINKEDIN}
      target="_blank"
      rel="noopener noreferrer"
      layout
      className={`group relative block rounded-3xl border p-5 transition-colors ${
        enAvant ? "border-cyan-300/40 bg-white/[0.06]" : "border-white/10 bg-white/[0.03]"
      }`}
    >
      {enAvant && (
        <div className="absolute -inset-px -z-10 rounded-3xl bg-gradient-to-br from-blue-500 via-cyan-400 to-violet-500 opacity-30 blur-xl" />
      )}
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-blue-500 via-cyan-400 to-violet-500 opacity-60 blur-md" />
          <img
            src={photo}
            alt="Adrien Giguelay"
            className="relative h-16 w-16 rounded-2xl border border-white/20 object-cover"
          />
        </div>
        <div className="min-w-0">
          <p className={`m-0 text-lg font-bold ${DEGRADE}`}>Adrien Giguelay</p>
          <p className="m-0 text-sm text-slate-300">Manager chez VIF · passionné d'IA</p>
        </div>
      </div>
      <p className="mb-4 mt-4 text-[15px] leading-relaxed text-slate-300">
        Veille, retours de terrain, projets perso. La conversation ne s'arrête
        pas à la fin de la conférence.
      </p>
      <div className="flex items-center justify-center gap-2 rounded-xl bg-[#0a66c2] px-4 py-3 font-semibold text-white transition-transform group-active:scale-[0.98]">
        <Linkedin size={18} />
        Me rejoindre sur LinkedIn
      </div>
    </motion.a>
  );
}

/* ───────────────────────── La page ───────────────────────── */

export default function SalleQuestions() {
  const parametres = new URLSearchParams(window.location.search);
  const session = parametres.get("s") || "seminaire";
  const cleProfil = `salle-profil-${session}`;
  const cleCode = `salle-code-${session}`;
  const reduit = useReducedMotion();

  const [profilEnvoye, setProfilEnvoye] = useState(() => lire(cleProfil) === "1");
  const [etape, setEtape] = useState(profilEnvoye ? "question" : "frequence");
  const [frequence, setFrequence] = useState(null);
  const [mot, setMot] = useState("");
  const [question, setQuestion] = useState("");
  const [site, setSite] = useState(""); // champ piège
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const [rang, setRang] = useState(null);
  const [passage, setPassage] = useState(0);
  const [nbEnvois, setNbEnvois] = useState(0);
  const [enDirect, setEnDirect] = useState(0);
  const [code, setCode] = useState(() => parametres.get("c") || lire(cleCode) || "");
  const [codeRequis, setCodeRequis] = useState(false);
  const champCode = useRef(null);
  const champQuestion = useRef(null);

  // Render gratuit dort : le premier appel le réveille pendant que la
  // personne choisit son niveau. Le compteur suit toutes les 30 s.
  useEffect(() => {
    let actif = true;
    const relever = () =>
      axios
        .get(`${API}/api/salle/direct`, { params: { session }, timeout: 90000 })
        .then((r) => actif && setEnDirect(r.data.envois || 0))
        .catch(() => {});
    relever();
    const minuteur = setInterval(relever, 30000);
    return () => { actif = false; clearInterval(minuteur); };
  }, [session]);

  useEffect(() => {
    if (etape === "question") champQuestion.current?.focus({ preventScroll: true });
  }, [etape]);

  const profil = profilEnvoye ? {} : { frequence, mot };
  const vide = !question.trim() && !(profil.frequence || (profil.mot || "").trim());

  const envoyer = async (e) => {
    e?.preventDefault();
    if (vide || envoi) return;
    setEnvoi(true);
    setErreur("");
    try {
      const { data } = await axios.post(
        `${API}/api/salle/reponse`,
        { session, question, site, code, ...profil },
        { timeout: 90000 }
      );
      // Le code a marché : il sert aux questions suivantes, même page rechargée.
      ecrire(cleCode, code);
      setCodeRequis(false);
      if (!profilEnvoye) {
        ecrire(cleProfil, "1");
        setProfilEnvoye(true);
      }
      setRang(data.rang ?? null);
      setQuestion("");
      setNbEnvois((n) => n + 1);
      setEnDirect((n) => n + 1);
      setPassage((n) => n + 1);
      try { navigator.vibrate?.(20); } catch { /* sans conséquence */ }
    } catch (err) {
      if (err.response?.data?.code_requis) {
        setCodeRequis(true);
        setTimeout(() => champCode.current?.focus(), 50);
      }
      setErreur(
        err.response?.data?.error ||
          "L'envoi n'a pas abouti. Votre texte est conservé, réessayez dans un instant."
      );
    } finally {
      setEnvoi(false);
    }
  };

  const transition = reduit ? { duration: 0 } : { duration: 0.35, ease: "easeOut" };
  const glisse = {
    initial: { opacity: 0, x: reduit ? 0 : 40 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: reduit ? 0 : -40 },
    transition,
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#050814] text-slate-100">
      <Fond />

      <div className="relative mx-auto flex min-h-screen max-w-md flex-col px-5 pb-10 pt-6">
        <header className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="m-0 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300/80">
              Séminaire IA
            </p>
            <Direct n={enDirect} />
          </div>
          <h1 className={`m-0 text-left text-[28px] font-extrabold leading-tight ${DEGRADE}`}>
            L'IA n'accélère pas ce que vous croyez
          </h1>
          {!profilEnvoye && <Progression etape={etape} />}
        </header>

        <main className="mt-8 flex-1">
          <AnimatePresence mode="wait">
            {etape === "frequence" && (
              <motion.section key="frequence" {...glisse} className="space-y-6">
                <div className="space-y-2">
                  <h2 className="m-0 text-2xl font-bold leading-snug text-white">
                    Vous, personnellement, vous utilisez l'IA…
                  </h2>
                  <p className="m-0 text-sm text-slate-400">Pas votre équipe. Vous.</p>
                </div>
                <Cuve valeur={frequence} onChange={setFrequence} reduit={reduit} />
                <Suite
                  onSuite={() => setEtape("mot")}
                  pret={!!frequence}
                />
              </motion.section>
            )}

            {etape === "mot" && (
              <motion.section key="mot" {...glisse} className="space-y-6">
                <h2 className="m-0 text-2xl font-bold leading-snug text-white">
                  L'IA, pour vous, en un mot ?
                </h2>
                <div className="relative py-6">
                  <input
                    type="text"
                    value={mot}
                    onChange={(e) => setMot(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && setEtape("question")}
                    maxLength={40}
                    autoFocus
                    enterKeyHint="next"
                    placeholder="…"
                    aria-label="L'IA en un mot"
                    className={`w-full border-0 bg-transparent text-center text-5xl font-extrabold outline-none placeholder:text-white/20 ${
                      mot ? DEGRADE : "text-white"
                    }`}
                  />
                  <div className="mx-auto mt-3 h-0.5 w-2/3 rounded-full bg-gradient-to-r from-transparent via-cyan-300 to-transparent opacity-70" />
                </div>
                <Suite
                  onSuite={() => setEtape("question")}
                  onRetour={() => setEtape("frequence")}
                  pret={!!mot.trim()}
                />
              </motion.section>
            )}

            {etape === "question" && (
              <motion.section key="question" {...glisse} className="space-y-6">
                <div className="space-y-2">
                  <h2 className="m-0 text-2xl font-bold leading-snug text-white">
                    {nbEnvois > 0 ? "Une autre question ?" : "Votre question sur l'IA"}
                  </h2>
                  <p className="m-0 text-[15px] leading-relaxed text-slate-400">
                    Celle que vous vous posez, ou celle que vous voudriez me poser.
                    Autant que vous voulez, jusqu'à la fin.
                  </p>
                </div>

                <form onSubmit={envoyer} className="space-y-6">
                  <div className="relative rounded-3xl border border-white/15 bg-white/[0.04] p-2 pl-4 shadow-[0_0_50px_-20px_rgba(96,165,250,0.6)] focus-within:border-cyan-300/60">
                    <textarea
                      ref={champQuestion}
                      value={question}
                      onChange={(e) => { setQuestion(e.target.value); setErreur(""); }}
                      maxLength={500}
                      rows={4}
                      placeholder="Posez-la comme vous la poseriez à la machine…"
                      aria-label="Votre question"
                      className="w-full resize-none border-0 bg-transparent py-2 pr-12 text-base leading-relaxed text-white outline-none placeholder:text-slate-500"
                    />
                    <button
                      type="submit"
                      disabled={vide || envoi}
                      aria-label="Envoyer"
                      className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 text-slate-950 shadow-lg transition-all enabled:active:scale-90 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500"
                    >
                      {envoi ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-950 border-t-transparent" />
                      ) : (
                        <ArrowUp size={20} strokeWidth={2.5} />
                      )}
                    </button>
                  </div>

                  <AnimatePresence>
                    {codeRequis && (
                      <motion.label
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={transition}
                        className="flex items-center gap-3 overflow-hidden rounded-2xl border border-amber-300/40 bg-amber-300/10 px-4 py-3"
                      >
                        <span className="shrink-0 text-sm text-amber-100">Code affiché à l'écran</span>
                        <input
                          ref={champCode}
                          type="text"
                          value={code}
                          onChange={(e) => { setCode(e.target.value); setErreur(""); }}
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          enterKeyHint="send"
                          aria-label="Code de la salle"
                          className="min-w-0 flex-1 border-0 bg-transparent text-right text-lg font-bold tracking-widest text-white outline-none placeholder:text-white/30"
                          placeholder="•••••"
                        />
                      </motion.label>
                    )}
                  </AnimatePresence>

                  {/* Champ piège : hors écran pour un humain, rempli par les robots. */}
                  <input
                    type="text"
                    name="site"
                    value={site}
                    onChange={(e) => setSite(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute -left-[9999px] h-0 w-0 opacity-0"
                  />

                  <Goulot passage={passage} reduit={reduit} />

                  <div className="min-h-[3rem] text-center" role="status" aria-live="polite">
                    <AnimatePresence mode="wait">
                      {erreur ? (
                        <motion.p key="erreur" {...glisse} className="m-0 text-sm text-rose-300">
                          {erreur}
                        </motion.p>
                      ) : nbEnvois > 0 ? (
                        <motion.p key={`merci-${nbEnvois}`} {...glisse} className="m-0 text-[15px] text-slate-300">
                          {rang ? (
                            <>
                              Passée par le goulot. Vous êtes la{" "}
                              <span className={`text-xl font-extrabold ${DEGRADE}`}>{rang}<sup>{rang === 1 ? "re" : "e"}</sup></span>{" "}
                              question.
                            </>
                          ) : (
                            "Merci, c'est bien arrivé."
                          )}
                          <br />
                          <span className="text-sm text-slate-500">Je vous réponds à la fin.</span>
                        </motion.p>
                      ) : null}
                    </AnimatePresence>
                  </div>
                </form>

                {!profilEnvoye && (
                  <button
                    type="button"
                    onClick={() => setEtape("mot")}
                    className="text-sm text-slate-500 underline-offset-4 hover:underline"
                  >
                    ← Revenir
                  </button>
                )}
              </motion.section>
            )}
          </AnimatePresence>
        </main>

        <footer className="mt-10 space-y-5">
          <AnimatePresence>
            {(nbEnvois > 0 || profilEnvoye) && (
              <motion.div
                initial={{ opacity: 0, y: reduit ? 0 : 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduit ? { duration: 0 } : { delay: nbEnvois > 0 ? 0.9 : 0, duration: 0.5 }}
              >
                <Carte enAvant={nbEnvois === 1} />
              </motion.div>
            )}
          </AnimatePresence>
          <p className="m-0 text-center text-xs leading-relaxed text-slate-500">
            Anonyme. Rien n'est nominatif. Les réponses sont supprimées après le séminaire.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Suite({ onSuite, onRetour, pret }) {
  return (
    <div className="flex items-center justify-between pt-2">
      {onRetour ? (
        <button type="button" onClick={onRetour} className="text-sm text-slate-500">
          ← Retour
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={onSuite}
        className={`flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-all active:scale-95 ${
          pret
            ? "bg-gradient-to-r from-blue-500 to-cyan-400 text-slate-950 shadow-[0_0_30px_-8px_rgba(34,211,238,0.8)]"
            : "border border-white/15 text-slate-400"
        }`}
      >
        {pret ? "Suivant" : "Passer"}
        <ArrowRight size={18} />
      </button>
    </div>
  );
}
